<script setup lang="ts">
import type { MediaFragment, ProductEntryFragment, ProductReviewDefaultFragment } from '#graphql-operations'

interface CardProductReviewProps {
  data: ProductReviewDefaultFragment
}

const props = defineProps<CardProductReviewProps>()

const productEntry = computed(() => props.data.products?.[0] as ProductEntryFragment | undefined)
const product = useProduct(() => productEntry.value)
const isCraftMedia = computed(() => !!product.value?.image && 'kind' in product.value.image)

const sizes = 'lg:40vw 1024px'
</script>

<template>
  <div v-if="product" class="cardProductReview c-$color-scheme-foreground gap-1.5 grid grid-cols-1 lg:gap-2.5 lg:grid-cols-2">
    <div v-if="isCraftMedia || product.image" v class="rounded-2.5 bg-$color-scheme-background aspect-square of-hidden">
      <CraftMedia
        v-if="isCraftMedia"
        :media="(product.image as MediaFragment)"
        :img-props="{ sizes, alt: product.title }"
        class="size-full object-cover"
      />
      <NuxtImg
        v-else-if="product.image"
        provider="shopify"
        :src="product.image.url"
        :alt="product.image.alt"
        :width="product.image.width"
        :height="product.image.height"
        :sizes="sizes"
        class="size-full object-cover"
      />
    </div>

    <div class="p-5 rounded-2.5 bg-$color-scheme-background flex flex-col gap-1.5 min-h-0 aspect-square items-start of-auto lg:gap-2.5">
      <Text v-if="product.brand || product.size" :html="[product.brand, product.size].filter(Boolean).join(' · ')" class="typo-sans-1 uppercase" />
      <Text v-if="product.subheading ?? product.heading" as="h3" :html="product.subheading ?? product.heading" class="typo-serif-2" />
      <div v-if="product.price" class="typo-serif-1 flex gap-1.5 items-baseline lg:gap-2.5">
        <Text :html="product.price" />
        <Text v-if="product.compareAtPrice" :html="product.compareAtPrice" class="line-through" />
      </div>
      <div v-if="product.links?.length" class="mt-auto flex flex-wrap gap-x-4 gap-y-1">
        <ButtonLink
          v-for="(link, index) in product.links"
          :key="index"
          :link="link"
          :title="link.label || $t('buy_at_retailer')"
          class="typo-sans-1 uppercase"
        />
      </div>
      <ButtonLink
        v-else-if="product.url"
        :to="product.url"
        :title="$t('buy_at_retailer')"
        class="typo-sans-1 mt-auto uppercase"
      />
    </div>

    <div v-if="data.heading || data.review" class="rounded-2.5 bg-$color-scheme-background flex min-h-0 aspect-square of-hidden">
      <div class="mt-auto p-5 flex flex-col gap-3.5 max-h-full of-auto lg:gap-2.5">
        <Text v-if="data.heading" :html="data.heading" class="typo-sans-1 uppercase" />
        <Text v-if="data.review" :html="data.review" class="typo-serif-1" />
      </div>
    </div>

    <div
      v-if="product.additionalInformations?.length"
      class="rounded-2.5 bg-$color-scheme-background flex min-h-0 aspect-square of-hidden"
    >
      <div class="mt-auto p-5 flex flex-col gap-3.5 max-h-full of-auto lg:gap-2.5">
        <div v-for="info in product.additionalInformations" :key="info.title">
          <Text
            :html="info.title" class="typo-sans-1 uppercase"
          />
          <Text :html="info.text" class="typo-serif-1" />
        </div>
      </div>
    </div>
  </div>
</template>
